/**
 * Bounds the camera/motion permission-recovery screen (VEX-7599).
 *
 * The screen posts no VERIFICATION_ERROR — that's what keeps the iframe alive so
 * iProov's permission_denied slot stays visible. The cost: IVA hears nothing, so
 * `live_selfie_attempts` never increments and the member never reaches
 * `live_selfie_failed!` → trusted referee. Unbounded, an OS-level camera block means
 * reloading into the same screen forever with no route out.
 *
 * Counted in sessionStorage because the host-page reload IS the recovery mechanism,
 * and it destroys in-memory state. Tab-scoped, so a new tab starts fresh.
 */

import { logger } from "./logger";

/**
 * The screen's only remedy is "change the setting, then reload." If one full reload
 * didn't clear it, the block is somewhere a reload can't reach and further attempts
 * just re-land the member here. Raising this trades escalation latency for retries
 * already shown not to work.
 */
export const RECOVERY_ATTEMPT_LIMIT = 1;

const STORAGE_KEY_PREFIX = "idme-permission-recovery:";

/**
 * The permission gates that get their own allowance. iOS prompts for camera and motion
 * separately, so a member can legitimately fail one, fix it, and then fail the other —
 * each deserves its own recovery screen.
 */
const RECOVERABLE_PERMISSIONS = ["camera", "motion"] as const;

// Fallback when sessionStorage throws (Safari Lockdown/private, some webviews). Does
// NOT survive the reload, so those browsers get the screen again rather than early
// escalation — preferring the recoverable case over the dead-end one.
const inMemoryCounts = new Map<string, number>();

function storageKey(permissionType: string): string {
  return `${STORAGE_KEY_PREFIX}${permissionType}`;
}

function readCount(permissionType: string): number {
  const key = storageKey(permissionType);
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? Number.parseInt(raw, 10) || 0 : 0;
  } catch {
    return inMemoryCounts.get(key) ?? 0;
  }
}

function writeCount(permissionType: string, count: number): void {
  const key = storageKey(permissionType);
  try {
    window.sessionStorage.setItem(key, String(count));
  } catch {
    inMemoryCounts.set(key, count);
  }
}

/** True once the member has used up their recovery-screen allowance. */
export function hasExhaustedPermissionRecovery(permissionType: string): boolean {
  return readCount(permissionType) >= RECOVERY_ATTEMPT_LIMIT;
}

/**
 * True if ANY gate's allowance is spent — for when we cannot tell which one failed.
 *
 * The caller classifies a denial from iProov's feedback code, falling back to a
 * user-agent latch when they send no `reasons`. That fallback is re-derived in a fresh
 * JS context after the recovery reload, so the same persistent denial can classify as
 * `camera` before the reload and `motion` after it. Keyed separately, neither count
 * reaches the limit and the member is reloaded twice — on exactly the browsers where a
 * reload cannot clear the denial at all.
 *
 * So a guessed classification must not mint a fresh allowance. An authoritative one
 * still checks only its own gate, which is what keeps the camera-then-motion case
 * working.
 */
export function hasExhaustedAnyPermissionRecovery(): boolean {
  return RECOVERABLE_PERMISSIONS.some(hasExhaustedPermissionRecovery);
}

/** Call when routing the member to the recovery screen. */
export function recordPermissionRecoveryAttempt(permissionType: string): void {
  const next = readCount(permissionType) + 1;
  writeCount(permissionType, next);
  logger.log(`[PermissionRecovery] ${permissionType} attempt ${next}/${RECOVERY_ATTEMPT_LIMIT}`);
}

/** Reset the allowance — on a passing capture, and after handing back to the error path. */
export function clearPermissionRecovery(permissionType: string): void {
  const key = storageKey(permissionType);
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    inMemoryCounts.delete(key);
  }
}

/**
 * Reset every gate's allowance.
 *
 * Used when escalating to the error path: the escalation may have been triggered by a
 * gate other than the one this denial was classified as, so clearing only the classified
 * one would leave the other counted. This is tab-scoped state, so a stale count would
 * deny a later attempt in the same tab its recovery screen entirely.
 */
export function clearAllPermissionRecovery(): void {
  RECOVERABLE_PERMISSIONS.forEach(clearPermissionRecovery);
}
